/**
 * Equipment Hooks
 *
 * TanStack Query hooks for equipment management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, buildQueryString, uploadFile } from '../client';
import type {
  Equipment,
  EquipmentListResponse,
  EquipmentFilters,
  CreateEquipmentRequest,
  UpdateEquipmentRequest,
  ImportResult,
} from '../types/equipment';

/** Query keys for equipment */
export const equipmentKeys = {
  all: ['equipment'] as const,
  lists: () => [...equipmentKeys.all, 'list'] as const,
  list: (filters: EquipmentFilters) => [...equipmentKeys.lists(), filters] as const,
  details: () => [...equipmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...equipmentKeys.details(), id] as const,
  byTag: (tagId: string) => [...equipmentKeys.all, 'byTag', tagId] as const,
};

/**
 * Fetch paginated equipment list
 */
export function useEquipment(filters: EquipmentFilters = {}) {
  return useQuery({
    queryKey: equipmentKeys.list(filters),
    queryFn: () =>
      apiFetch<EquipmentListResponse>(`/api/equipment${buildQueryString(filters)}`),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Fetch single equipment by ID
 */
export function useEquipmentDetail(id: string) {
  return useQuery({
    queryKey: equipmentKeys.detail(id),
    queryFn: () => apiFetch<Equipment>(`/api/equipment/${id}`),
    enabled: !!id,
  });
}

/**
 * Fetch equipment by tag ID (for kiosk scanning)
 */
export function useEquipmentByTag(tagId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: equipmentKeys.byTag(tagId),
    queryFn: () =>
      apiFetch<Equipment>(`/api/equipment${buildQueryString({ search: tagId, limit: 1 })}`).then(
        (response) => {
          // Return the first matching equipment or null
          const items = (response as unknown as EquipmentListResponse).items;
          return items.length > 0 ? items[0] : null;
        }
      ),
    enabled: options?.enabled !== false && !!tagId && tagId.length >= 3,
    staleTime: 10_000, // 10 seconds
  });
}

/**
 * Create new equipment
 */
export function useCreateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEquipmentRequest) =>
      apiFetch<Equipment>('/api/equipment', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}

/**
 * Update existing equipment
 */
export function useUpdateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateEquipmentRequest & { id: string }) =>
      apiFetch<Equipment>(`/api/equipment/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: equipmentKeys.detail(variables.id) });
    },
  });
}

/**
 * Delete equipment
 */
export function useDeleteEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/equipment/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}

/**
 * Import equipment from CSV
 */
export function useImportEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadFile<ImportResult>('/api/equipment/import', file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}
